import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

// Esta flag garante que o processo de autenticação seja executado apenas uma vez,
// mesmo com o comportamento de desmontar/remontar do React.StrictMode em desenvolvimento.
let authProcessed = false;

const Callback = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    // Log inicial para depuração em produção
    console.log('[OAuth/Callback] Mounted with location:', {
      pathname: location.pathname,
      search: location.search,
      origin: window.location.origin,
    });
    // Se o callback já foi processado nesta sessão da página, não faz nada.
    if (authProcessed) {
      // Se chegamos aqui sem um usuário, significa que a tentativa anterior falhou.
      // A melhor ação é voltar para a tela de login.
      navigate('/login');
      return;
    }

    const processOAuthCallback = async () => {
      try {
        // Primeiro, verificar se já existe uma sessão ativa
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        if (existingSession?.user) {
          console.log('✅ Sessão já existe, redirecionando para home...');
          navigate('/');
          return;
        }

        // Marca como processado apenas se não há sessão existente
        authProcessed = true;

        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get('code');
        const errorDescription = searchParams.get('error_description');
        const error = searchParams.get('error');
        const flowStateId = searchParams.get('flow_state_id');
        console.log('[OAuth/Callback] Query params parsed:', {
          hasCode: !!code,
          codeLength: code?.length,
          error,
          errorDescription,
          flowStateId,
        });
        
        // Se há erro do provedor OAuth, mostrar e redirecionar
        if (error || errorDescription) {
          const errorMsg = errorDescription ? decodeURIComponent(errorDescription) : (error || "Erro desconhecido");
          console.error('❌ Erro do provedor OAuth:', errorMsg);
          
          setError(errorMsg);
          setLoading(false);
          
          toast({
            variant: "destructive",
            title: "Erro de Autenticação",
            description: errorMsg,
          });
          
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Se não houver código, redireciona para o login.
        if (!code) {
          console.warn('⚠️ Nenhum código de autorização recebido. Redirecionando para login.');
          navigate('/login');
          return;
        }

        // Verificar se o código já foi usado (pode acontecer em recarregamentos)
        if (code.length < 10) {
          console.warn('⚠️ Código de autorização inválido ou já usado. Redirecionando para login.');
          navigate('/login');
          return;
        }

        // ✅ Processar o código de autorização
        console.log('🔄 Trocando código de autorização por uma sessão...', {
          codeSnippet: code.substring(0, 6) + '...'
        });
        
        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        console.log('[OAuth/Callback] Exchange response:', {
          hasSession: !!data?.session,
          hasUser: !!data?.user,
          provider: 'azure',
        });
        
        if (exchangeError) {
          console.error('❌ Erro ao trocar o código pela sessão:', exchangeError);
          throw exchangeError;
        }
        
        if (data.session && data.user) {
          console.log('✅ Usuário autenticado com sucesso:', data.user.email);
          
          // Verificar e atribuir role se necessário
          try {
            const { data: existingRoles } = await supabase
              .from('user_roles')
              .select('role_id')
              .eq('user_id', data.user.id);
              
            if (!existingRoles || existingRoles.length === 0) {
              const { data: userRole } = await supabase
                .from('roles')
                .select('id')
                .eq('name', 'User')
                .single();
                
              if (userRole) {
                await supabase
                  .from('user_roles')
                  .insert({
                    user_id: data.user.id,
                    role_id: userRole.id,
                    created_by: data.user.id,
                    updated_by: data.user.id
                  });
                console.log('👤 Role "User" atribuída ao novo usuário.');
              }
            }
          } catch (roleError) {
            console.warn('⚠️ Erro ao atribuir role. O login continuará.', roleError);
          }
          
          toast({
            title: "Login bem-sucedido!",
            description: "Você foi autenticado com sucesso via Microsoft.",
          });
          
          // Redirecionar para home
          navigate('/');
        } else {
          throw new Error('Sessão não foi criada após a troca do código.');
        }
      } catch (err: any) {
        console.error('❌ Erro crítico no callback OAuth:', {
          message: err?.message,
          stack: err?.stack,
        });
        setError(err.message || 'Ocorreu um erro durante a autenticação.');
        setLoading(false);
        
        toast({
          variant: "destructive",
          title: "Erro de Autenticação",
          description: err.message || "Por favor, tente fazer o login novamente.",
        });
        
        // Limpa a flag em caso de erro crítico para permitir uma nova tentativa.
        authProcessed = false;
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processOAuthCallback();
  }, [location, navigate, toast]);

  // Resetar a flag quando o usuário navegar para longe desta página.
  useEffect(() => {
    return () => {
      authProcessed = false;
    };
  }, []);

  if (loading && !error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
        <p className="text-lg">Finalizando autenticação...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="text-center p-4">
          <h1 className="text-2xl font-extrabold text-destructive mb-4">Erro de Autenticação</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <p className="text-sm text-muted-foreground">Você será redirecionado para a página de login.</p>
        </div>
      </div>
    );
  }

  return null;
};

export default Callback; 